/**
 * Google Places autocomplete renders its suggestion list into a `.pac-container`
 * element appended to `document.body` — outside the React tree, and so outside
 * whatever Radix layer the input itself lives in. Radix's dismissable layer sees
 * a pointer-down on a suggestion as an OUTSIDE interaction and closes the
 * dialog/sheet before the selection ever applies, discarding the form the user
 * was partway through filling in (NOT-523).
 *
 * The click is not really outside: it is the user operating a control that
 * belongs to a field inside the layer. These helpers reclassify it as such.
 * They are deliberately narrow — the predicate is only true while a Places
 * dropdown is on screen AND the event target is inside it, so a layer with no
 * address field behaves exactly as before.
 *
 * Applied in the `DialogContent` and `SheetContent` atoms, which is every
 * dismissable layer that hosts an address form. `AlertDialogContent` needs
 * nothing — Radix already `preventDefault`s its outside interactions, so it
 * cannot be dismissed this way. `PopoverContent` is deliberately left alone: no
 * popover contains an address field, and popovers exist to dismiss eagerly.
 * Put an address field in one and it will need this guard.
 *
 * The CSS half of the fix lives in `index.css` (`.pac-container` re-enables
 * `pointer-events`, which Radix strips from `body` while a modal layer is open).
 * Neither half works without the other: without the CSS the suggestion can't be
 * clicked, without this guard the click closes the layer.
 */

/** True when `target` is inside a Google Places `.pac-container` dropdown. */
export function isPlacesDropdownTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('.pac-container'));
}

/**
 * The shape Radix hands to `onPointerDownOutside` / `onInteractOutside`.
 * Structural so this module doesn't depend on Radix's internal event types.
 */
type OutsideInteractionEvent = {
  detail: { originalEvent: { target: EventTarget | null } };
  preventDefault: () => void;
};

/**
 * Wraps a Radix outside-interaction handler so interactions with a Places
 * suggestion dropdown do not dismiss the layer. Any other outside interaction
 * falls through to `handler` (and, if that doesn't `preventDefault`, dismisses
 * as usual).
 */
export function withPlacesDropdownGuard<Event extends OutsideInteractionEvent>(
  handler: ((event: Event) => void) | undefined,
): (event: Event) => void {
  return (event: Event) => {
    if (isPlacesDropdownTarget(event.detail.originalEvent.target)) {
      event.preventDefault();
      return;
    }
    handler?.(event);
  };
}
